import React, { useState } from 'react'
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom'
import { Stack, Typography, Box, Link, alpha, useTheme } from '@mui/material'
import { Alert } from '@repo/ui'
import { workosAuthService } from 'services/workos-auth.service'
import { ROUTES } from 'routes'
import { PASSWORD_MIN_LENGTH, passwordMeetsPolicy, mapWorkosPasswordError } from 'constants/passwordPolicy'
import { AuthLogo } from './components/AuthLogo'
import { authGlassCardSx } from './components/authGlassCard'
import { AuthPasswordField } from './components/AuthPasswordField'
import { AuthPrimaryButton } from './components/AuthPrimaryButton'
import { AUTH_FIELD_LABEL_SX, AUTH_TITLE_SX } from './components/authType'
import { PasswordStrengthMeter } from './components/PasswordStrengthMeter'

/**
 * VERA-1095: this page now serves two flows behind one component —
 * 1. `PASSWORD_SETUP` (authenticated, "add a password" onboarding step —
 *    the original behaviour, gated by ProtectedRoute).
 * 2. `RESET_PASSWORD` (unauthenticated "forgot password" completion — the
 *    emailed link lands here with `?token=`, gated by UnProtectedRoute).
 * Reset mode is determined by ROUTE, not just token presence, so a reset
 * link opened with a missing/stripped token still shows the "invalid link"
 * state rather than silently falling through to the authenticated path
 * (which would just fail with "Not authenticated").
 */
export const PasswordSetup = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isResetMode = location.pathname === ROUTES.RESET_PASSWORD
  const resetToken = searchParams.get('token')
  const isInvalidResetLink = isResetMode && !resetToken

  // VRG-339 (from main): `/` decides. Both exits below go there rather than
  // re-deriving a destination from the user's role — this page used to branch
  // internal-vs-company itself, which duplicated the one decision `/` owns.
  const homePath = ROUTES.HOME

  const handleSkip = () => {
    navigate(homePath, { replace: true })
  }

  // VERA-1095: the one shared client-side password policy (constants/passwordPolicy.ts) —
  // see that file's header comment for why this is safe against the backend
  // (and WorkOS) without a backend change, and why it's also wired into
  // SignUpPage's password field so the two surfaces agree.
  const passwordInvalid = password.length > 0 && !passwordMeetsPolicy(password)
  const confirmInvalid = confirm.length > 0 && password !== confirm

  const handleSubmit = async () => {
    setError(null)

    if (!passwordMeetsPolicy(password)) {
      setError('Password does not meet the requirements below.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }

    setIsSubmitting(true)
    const result = isResetMode && resetToken
      ? await workosAuthService.confirmPasswordReset(resetToken, password)
      : await workosAuthService.setupPassword(password)
    setIsSubmitting(false)

    if (result.success) {
      if (isResetMode) {
        // No session was ever established here (the token stands in for
        // auth) — route back to sign-in so the user can log in with the new
        // password, rather than a "home" path that assumes a logged-in user.
        navigate(ROUTES.SIGN_IN, { replace: true })
      } else {
        navigate(homePath, { replace: true })
      }
    } else {
      // VERA-1095: WorkOS's raw strength-rejection string ("Password does not
      // meet strength requirements.") was previously relayed verbatim, with
      // no statement of what the requirements actually were. By this point the
      // password has already passed every PASSWORD_REQUIREMENTS rule (submit
      // is blocked above otherwise), so a residual rejection here is most
      // likely WorkOS's own common/breached-password check — mapped to
      // something actionable rather than passed through.
      setError(mapWorkosPasswordError(result.error) || 'Failed to set password. Please try again.')
    }
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        // VERA-1095: backdrop hoisted to LayoutAuthAmbient, which owns it
        // theme-aware for every auth route. This shell is layout only.
        bgcolor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        // VRG-646: phone gutter. The 396px column flex-shrinks to the viewport,
        // and without padding here it ran flush to both screen edges at 375px
        // (measured: column 0..375). Same 12px the callback shell uses.
        px: 3,
      }}
    >
      {/* VERA-1095: the page-local blob pair was DELETED here. These pages are
          routed under `LayoutAuthAmbient`, which renders the shared
          `SignInBackground` for every auth screen — so this page was drawing a
          second, STATIC copy of the same two blobs on top of the shared
          animated ones, which is why this backdrop read lighter and more purple
          than sign-in's. The shared one is strictly better: it orbits, it
          breathes, and it survives navigation. */}

      {/* Main content */}
      <Box
        sx={{
          // Phones only: the solid glass sheet every auth page has below `sm`
          // (authGlassCard.ts, 2026-10-08). This page never had a desktop card, and
          // desktop is unchanged. FIRST, so `position`/`zIndex` below win.
          ...authGlassCardSx(theme, { desktop: false }),
          position: 'relative',
          zIndex: 2,
          width: '396px',
          display: 'flex',
          flexDirection: 'column',
          gap: '60px',
          alignItems: 'center',
        }}
      >
        {/* Logo — `AuthLogo` rather than a third hand-rolled copy of the same
            mark; see the note on VerifyEmailPage's. */}
        <AuthLogo />

        {/* Form Container */}
        <Stack spacing={3} width="100%">
          {isInvalidResetLink ? (
            // VERA-1095: reached /reset-password with no (or a stripped)
            // token — the link is unusable. Show this instead of silently
            // falling through to the authenticated setupPassword path,
            // which would just fail with "Not authenticated".
            <Stack spacing={2}>
              <Typography sx={AUTH_TITLE_SX}>
                This link is invalid
              </Typography>
              <Typography sx={{ fontSize: '14px', color: 'text.secondary', lineHeight: 1.5 }}>
                This password reset link is missing or malformed. Request a new one from the sign-in page.
              </Typography>
              <Box sx={{ textAlign: 'center', py: '4px' }}>
                <Link
                  component="button"
                  onClick={() => navigate(ROUTES.SIGN_IN, { replace: true })}
                  sx={{
                    color: 'text.primary',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    fontSize: '14px',
                    lineHeight: 1.4,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Back to sign in
                </Link>
              </Box>
            </Stack>
          ) : (
            <>
              {error && (
                // The fill override is a leftover from when this page painted
                // its own black shell and the Alert's own (light-theme) tint
                // had nothing to sit on. The shell is theme-aware now, so the
                // Alert's tint would resolve correctly on its own and this is
                // very likely redundant — kept, tokenised, rather than deleted,
                // because removing it is a visual change and this pass is
                // colour tokenisation only. `alpha(error.main, 0.1)` reproduces
                // the literal EXACTLY in light mode (error.main is #d32f2f =
                // rgb(211,47,47)) and gives the brighter dark-mode red on dark.
                <Alert
                  severity="error"
                  description={error}
                  onClose={() => setError(null)}
                  sx={{ backgroundColor: alpha(theme.palette.error.main, 0.1) }}
                />
              )}

          <Typography
            sx={{
              ...AUTH_TITLE_SX,
            }}
          >
            {isResetMode ? 'Reset your password' : 'Create a password'}
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Lato, sans-serif',
              fontSize: '14px',
              lineHeight: 1.4,
              color: 'text.secondary',
            }}
          >
            {isResetMode
              ? 'Choose a new password for your account.'
              : 'Set a password so you can sign in with your email and password in the future.'}
          </Typography>

          {/* Password field */}
          <Stack spacing={2}>
            <Typography
              sx={AUTH_FIELD_LABEL_SX}
            >
              {isResetMode ? 'New password' : 'Create password'}
            </Typography>
            <AuthPasswordField
              name="password"
              placeholder={`At least ${PASSWORD_MIN_LENGTH} characters`}
              value={password}
              onChange={setPassword}
            />
            {/* VERA-1095 items 3 + 5: requirements stated inline as the user
                types (not only after a failed submit), plus a strength
                meter — both read the exact same PASSWORD_REQUIREMENTS the
                submit button gates on below, so they can never disagree with
                what's actually accepted. */}
            <PasswordStrengthMeter password={password} />
          </Stack>

          {/* Confirm password field */}
          <Stack spacing={2}>
            <Typography
              sx={AUTH_FIELD_LABEL_SX}
            >
              Confirm password
            </Typography>
            <AuthPasswordField
              name="confirmPassword"
              placeholder="Repeat your password"
              value={confirm}
              onChange={setConfirm}
              onKeyPress={(e) => {
                if (e.key === 'Enter') handleSubmit()
              }}
              error={confirmInvalid}
              helperText={confirmInvalid ? 'Passwords do not match' : undefined}
            />
          </Stack>

          {/* Submit button — disabled while either field currently has a
              validation error (house rule: disable Save/submit on field
              error), not just while empty. */}
          <AuthPrimaryButton
            fullWidth
            onClick={handleSubmit}
            disabled={!password || !confirm || passwordInvalid || confirmInvalid}
            loading={isSubmitting}
          >
            {isResetMode ? 'Reset password' : 'Set password'}
          </AuthPrimaryButton>

          {/* Skip link — only in the authenticated onboarding step; reset
              mode has no session to skip out of. */}
          {!isResetMode && (
            <Box sx={{ textAlign: 'center', py: '4px' }}>
              <Link
                component="button"
                onClick={handleSkip}
                sx={{
                  color: 'text.secondary',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: 1.4,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Skip for now
              </Link>
            </Box>
          )}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  )
}
